import { LocalDate } from "@js-joda/core";
import { Text } from "components/Typography/text.styled";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import {
  ArrowSign,
  Cell,
  DeductionAmountContainer,
  DividerLine1,
  DividerLine2,
  DividerLine3,
  EmptyDiv,
  GrossSalaryContainer,
  HoursContainer,
  Logo,
  LogoContainer,
  MonthSelectContainer,
  NetSalaryContainer,
  PayrollTaxContainer,
  Row,
  SalaryActionContainer,
  SalaryNoticeContainer,
  SalaryPageContainer,
  TaxContainer,
  TaxTableContainer,
  WorkerContainer,
  TooltipModal,
  TooltipContainer,
} from "./salary.styled";
import { FontFamily, FontSize } from "config/font";
import { Api } from "services/api/api.service";
import { Pagination } from "../general/pagination.component";
import { SalaryNotice } from "model/Salary";
import { Color } from "config/colors";
import { IconCircularContainer } from "../dashboard/dashboard.styled";
import { CustomIcon } from "components/icon/customIcon.component";
import { formatDuration } from "utils/utils";
import { PaymentMethod } from "model/Gig";
import SimflexityPayLogo from "images/logo/svg/simflexityPay-logo.svg";
import { FileViewer } from "../fileViewer/fileViewer.component";
import Modal from "@material-ui/core/Modal/Modal";
import { LinkWrapper } from "../gig/gigDetail/gigDetail.styled";
import { handleNextMonth, handlePreviousMonth } from "./agiOverview.component";
import { ModalState } from "./individualSalary.component";

export const MonthlySalary = () => {
  const { t } = useTranslation();
  const [currentDate, setCurrentDate] = useState(LocalDate.now());
  const [{ company }] = useCompanyStore();
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [salaryNotices, setSalaryNotices] = useState<SalaryNotice[]>([]);
  const [showInfo, setShowInfo] = useState(false);
  const [modalState, setModalState] = useState<ModalState>({
    open: false,
    workerId: undefined,
    pdfId: undefined,
    period: undefined,
    workerName: undefined,
  });

  const fetchAmount = 25;
  const formattedDate = useMemo(
    () =>
      `${t(`CompanyTimeReport.${currentDate.month()}`)} ${currentDate.year()}`,
    [currentDate, t]
  );

  const getSalaries = useCallback(() => {
    if (!company?.id) return;

    Api()
      .company.salary.getSalaries(
        company.id,
        page,
        fetchAmount,
        currentDate.year(),
        currentDate.monthValue()
      )
      .then((res) => {
        setSalaryNotices(res.data.salaryNotices);
        setTotalPages(Math.ceil(res.data.salaryNotices.length / fetchAmount));
      })
      .catch((e) => {
        console.error("Error when getting salaries", e);
      });
  }, [company?.id, page, currentDate]);

  const openSalaryPdf = useCallback((salary: SalaryNotice) => {
    setModalState({
      open: true,
      workerId: salary.workerId,
      pdfId: salary.id,
      period: salary.period,
      workerName: salary.workerName,
    });
  }, []);

  const closeModal = useCallback(() => {
    setModalState((prev) => ({ ...prev, open: false }));
  }, []);

  useEffect(() => {
    if (!modalState.open) {
      setModalState((prev) => ({ ...prev, pdfId: undefined }));
    }
  }, [modalState.open]);

  useEffect(() => {
    getSalaries();
  }, [getSalaries]);

  const renderSalaryCell = useCallback(
    (salary: SalaryNotice, index: number) => {
      const currency = salary.currency === "SEK" ? " kr" : "";

      return (
        <Cell key={`monthlysalary-${salary.id || index}`}>
          <WorkerContainer>
            <Text
              style={{ margin: 0, padding: 0 }}
              fontSize={FontSize.Small}
              color={Color.SeaBlue600}
            >
              {t("General.Giggers")}
            </Text>
            <Text
              color={Color.MidnightBlue}
              fontFamily={FontFamily.MontserratRegular}
            >
              {salary.workerName}
            </Text>
          </WorkerContainer>
          <DividerLine1 />
          <HoursContainer>
            <Text
              style={{ margin: 0, padding: 0 }}
              fontSize={FontSize.Small}
              color={Color.SeaBlue600}
            >
              {t("Salary.TotalWorkTime")}
            </Text>
            <Text
              color={Color.MidnightBlue}
              fontFamily={FontFamily.MontserratRegular}
            >
              {formatDuration(salary.workedHours.toString())}
            </Text>
          </HoursContainer>
          <DividerLine2 />
          <GrossSalaryContainer>
            <Text
              style={{ margin: 0, padding: 0 }}
              fontSize={FontSize.Small}
              color={Color.SeaBlue600}
            >
              {t("Salary.GrossSalary")}
            </Text>
            <Text
              color={Color.MidnightBlue}
              fontFamily={FontFamily.MontserratRegular}
            >
              {`${salary.grossSalary}${currency}`}
            </Text>
          </GrossSalaryContainer>
          <NetSalaryContainer>
            <Text
              style={{ margin: 0, padding: 0 }}
              fontSize={FontSize.Small}
              color={Color.SeaBlue600}
            >
              {t("Salary.NetSalary")}
            </Text>
            <Text
              color={Color.MidnightBlue}
              fontFamily={FontFamily.MontserratRegular}
            >
              {`${salary.netSalary}${currency}`}
            </Text>
          </NetSalaryContainer>
          <TaxContainer>
            <Text
              style={{ margin: 0, padding: 0 }}
              fontSize={FontSize.Small}
              color={Color.SeaBlue600}
            >
              {t("Salary.Tax")}
            </Text>
            <Text
              color={Color.MidnightBlue}
              fontFamily={FontFamily.MontserratRegular}
            >
              {`${salary.preliminaryTax}${currency}`}
            </Text>
          </TaxContainer>
          <PayrollTaxContainer>
            <Text
              style={{ margin: 0, padding: 0 }}
              fontSize={FontSize.Small}
              color={Color.SeaBlue600}
            >
              {t("Salary.PayrollTax")}
            </Text>
            <Text
              color={Color.MidnightBlue}
              fontFamily={FontFamily.MontserratRegular}
            >
              {`${salary.payrollTax}${currency}`}
            </Text>
          </PayrollTaxContainer>
          <DeductionAmountContainer>
            <Text
              style={{ margin: 0, padding: 0 }}
              fontSize={FontSize.Small}
              color={Color.SeaBlue600}
            >
              {t("Salary.DeductionAmount")}
            </Text>
            <Text
              color={Color.MidnightBlue}
              fontFamily={FontFamily.MontserratRegular}
            >
              {`${salary.deductionAmount}${currency}`}
            </Text>
          </DeductionAmountContainer>
          <TaxTableContainer>
            <Text
              style={{ margin: 0, padding: 0 }}
              fontSize={FontSize.Small}
              color={Color.SeaBlue600}
            >
              {salary.payrollType === PaymentMethod.SIMFLEXITY_PAY
                ? t("Salary.TaxProcent")
                : t("Salary.TaxTable")}
            </Text>
            <Text
              color={Color.MidnightBlue}
              fontFamily={FontFamily.MontserratRegular}
            >
              {salary.payrollType === PaymentMethod.SIMFLEXITY_PAY
                ? salary.taxPercentage + "%"
                : salary.taxTable}
            </Text>
          </TaxTableContainer>
          <DividerLine3 />
          <SalaryActionContainer>
            <IconCircularContainer
              isAccept
              onClick={() => openSalaryPdf(salary)}
            >
              <CustomIcon
                className="icon"
                name="document"
                size="20px"
                padding="2px"
                color={Color.White}
              />
            </IconCircularContainer>

            {salary.payrollType === PaymentMethod.SIMFLEXITY_PAY ? (
              <LogoContainer>
                <Logo src={SimflexityPayLogo} alt="SimflexityPay logo" />
              </LogoContainer>
            ) : (
              <EmptyDiv />
            )}
          </SalaryActionContainer>
        </Cell>
      );
    },
    [t, openSalaryPdf]
  );

  const renderSalaryNotices = useCallback(() => {
    if (salaryNotices.length === 0) {
      return (
        <Text style={{ textAlign: "center", marginTop: 40 }}>
          {t("Salary.PeriodNotClosed", {
            month: `${t(
              `CompanyTimeReport.${currentDate.month()}`
            ).toLowerCase()}`,
          })}
        </Text>
      );
    }

    return (
      <>
        <SalaryNoticeContainer>
          {salaryNotices.map(renderSalaryCell)}
        </SalaryNoticeContainer>
        <Pagination setPage={setPage} page={page} totalPages={totalPages} />
      </>
    );
  }, [
    salaryNotices,
    renderSalaryCell,
    t,
    currentDate,
    page,
    totalPages,
    setPage,
  ]);

  const renderPdfModal = useCallback(() => {
    const { open, workerId, pdfId, period, workerName } = modalState;

    if (!open || !company || !workerId || !pdfId || !period || !workerName) {
      return null;
    }

    return (
      <Modal
        open={open}
        onClose={closeModal}
        aria-labelledby="file-viewer-modal"
        aria-describedby="file-viewer-description"
      >
        <>
          <LinkWrapper>
            <CustomIcon
              color={Color.LighterDestructive}
              onClick={closeModal}
              name="cross"
            >
              {t("General.Close")}
            </CustomIcon>
          </LinkWrapper>
          <FileViewer
            fetchEndpoint={() =>
              Api().company.salary.generateSalaryNotice(
                company.id,
                workerId,
                pdfId
              )
            }
            filename={`SalaryNotice_${period}_${workerName}.pdf`}
            type="application/pdf"
          />
        </>
      </Modal>
    );
  }, [modalState, company, closeModal, t]);

  return (
    <SalaryPageContainer>
      <TooltipContainer>
        <Row
          onMouseEnter={() => setShowInfo(true)}
          onMouseLeave={() => setShowInfo(false)}
          style={{ cursor: "pointer" }}
        >
          <Text
            style={{ textAlign: "right" }}
            fontFamily={FontFamily.MontserratSemiBold}
            fontSize={FontSize.Small}
          >
            {t(`Salary.WhatIs`, {
              name: t("Salary.MonthlySalary").toLowerCase(),
            })}
          </Text>
        </Row>

        {showInfo && (
          <TooltipModal
            onMouseEnter={() => setShowInfo(true)}
            onMouseLeave={() => setShowInfo(false)}
          >
            <Text fontSize={FontSize.Small}>
              {t("Salary.MonthlySalarySlipInfo")}
            </Text>
          </TooltipModal>
        )}
      </TooltipContainer>

      <MonthSelectContainer>
        <ArrowSign onClick={() => handlePreviousMonth(setCurrentDate)}>
          &#60;
        </ArrowSign>
        <Text
          fontSize={FontSize.H4}
          fontFamily={FontFamily.MontserratSemiBold}
          style={{ margin: 0 }}
        >
          {formattedDate}
        </Text>
        <ArrowSign onClick={() => handleNextMonth(setCurrentDate)}>
          &#62;
        </ArrowSign>
      </MonthSelectContainer>

      {renderSalaryNotices()}
      {renderPdfModal()}
    </SalaryPageContainer>
  );
};
